/**
 * Role Prioritisation — the whole calculation, kept pure so it can be tested against the brief's
 * acceptance criteria without rendering anything.
 *
 * All figures are hours per week. Compute from raw values and round only for display: the C tier
 * costs 1.15 h and must never render as 1.2, which is the bug the prototype shipped with.
 */
import {
  ACTIVITIES,
  FIREFIGHTING_GUARDRAIL_PCT,
  RATINGS,
  type Allocation,
  type CapacityAssumptions,
  type ClusterSeed,
  type EffortModel,
  type LoadStatus,
  type Portfolio,
  type PrioritisationState,
  type Rating,
} from '../types/rolePrioritisation';

// ─────────────────────────── display helpers ───────────────────────────

/** At most two decimals, trailing zeros stripped: 1.15 stays 1.15, 20.00 becomes 20. */
export function hoursLabel(v: number): string {
  if (!isFinite(v)) return '—';
  return `${Math.round(v * 100) / 100}`;
}

/** Whole percent. Returns null when there is no capacity to divide by, so callers show a dash. */
export function pctLabel(v: number | null): string {
  return v == null || !isFinite(v) ? '—' : `${Math.round(v)}%`;
}

export function fteLabel(v: number): string {
  return isFinite(v) ? v.toFixed(1) : '—';
}

// ─────────────────────────── effort and demand ───────────────────────────

export function tierHours(model: EffortModel, r: Rating): number {
  const e = model[r];
  return ACTIVITIES.reduce((s, a) => s + (e[a.id] || 0), 0);
}

export function tierLoad(model: EffortModel, portfolio: Portfolio, r: Rating): number {
  return tierHours(model, r) * (portfolio[r] || 0);
}

export function demandOf(model: EffortModel, portfolio: Portfolio): number {
  return RATINGS.reduce((s, r) => s + tierLoad(model, portfolio, r), 0);
}

export function openRolesOf(portfolio: Portfolio): number {
  return RATINGS.reduce((s, r) => s + (portfolio[r] || 0), 0);
}

/** The approved portfolio plus whatever the scenario is asking about. */
export function effectivePortfolio(portfolio: Portfolio, added: Portfolio): Portfolio {
  const out = {} as Portfolio;
  RATINGS.forEach((r) => {
    out[r] = (portfolio[r] || 0) + (added[r] || 0);
  });
  return out;
}

// Capacity, team composition and the intern pool live in their own module; re-exported here so
// that "the prioritisation maths" stays one import for every caller. The explicit import is
// separate because `export *` re-exports without binding the names in this module's own scope.
export * from './rolePrioritisationTeam';
import {
  capacityYield,
  fteHours,
  grossHours,
  headsIn,
  hoursLentBy,
  isPlanned,
  personCapacity,
  poolAllocated,
  poolAvailableFrom,
  volunteeredHours,
} from './rolePrioritisationTeam';

// ─────────────────────────── status ───────────────────────────

export function statusOf(utilisation: number | null): LoadStatus {
  if (utilisation == null) return 'headroom';
  if (utilisation > 100) return 'over';
  if (utilisation > 85) return 'limit';
  return 'headroom';
}

export const STATUS_LABEL: Record<LoadStatus, string> = {
  headroom: 'Headroom',
  limit: 'At the limit',
  over: 'Overcommitted',
};

// ─────────────────────────── per cluster ───────────────────────────

export interface ClusterStats {
  key: string;
  roles: Portfolio;
  roleCount: number;
  demand: number;
  /** Capacity of the cluster's own people, before any support moves. */
  own: number;
  internHours: number;
  outHours: number;
  lent: number;
  borrowed: number;
  committed: number;
  spare: number;
  utilisation: number | null;
  status: LoadStatus;
}

export function clusterStats(
  cluster: ClusterSeed,
  state: PrioritisationState,
  model: EffortModel,
  cap: CapacityAssumptions
): ClusterStats {
  const demand = demandOf(model, cluster.roles);
  const own = headsIn(state.team, cluster.key).reduce((s, p) => s + personCapacity(p, cap), 0);
  const internHours = (state.interns[cluster.key] || 0) * fteHours(cap);
  const outHours = (state.out[cluster.key] || 0) * fteHours(cap);
  /**
   * ⚠️ Only a PLANNED lender's hours are debited from their cluster.
   *
   * Someone marked `countsTowardHiring: false` — the Head of TA — was never in any cluster's
   * capacity, so lending their time is NET-NEW capacity, not a transfer. Debiting their cluster
   * would take away hours it never had, and would make the lead's offer of help read as a cost to
   * her own team.
   */
  const lent = state.allocations.reduce((s, a) => {
    const person = state.team[a.from];
    return s + (person && isPlanned(person) && person.cluster === cluster.key ? a.hours : 0);
  }, 0);
  const borrowed = state.allocations.reduce((s, a) => s + (a.to === cluster.key ? a.hours : 0), 0);
  const committed = own + internHours - outHours - lent + borrowed;
  const utilisation = committed > 0 ? (demand / committed) * 100 : null;
  return {
    key: cluster.key,
    roles: cluster.roles,
    roleCount: openRolesOf(cluster.roles),
    demand,
    own,
    internHours,
    outHours,
    lent,
    borrowed,
    committed,
    spare: committed - demand,
    utilisation,
    status: statusOf(utilisation),
  };
}

export function allClusterStats(
  clusters: ClusterSeed[],
  state: PrioritisationState,
  model: EffortModel,
  cap: CapacityAssumptions
): ClusterStats[] {
  return clusters.map((c) => clusterStats(c, state, model, cap));
}

// ─────────────────────────── company wide ───────────────────────────

export interface CompanyStats {
  /** Hours lent by people who are on the team but not planned as hiring capacity. */
  volunteered: number;
  portfolio: Portfolio;
  openRoles: number;
  demand: number;
  /** Base capacity from contracted hours, before interns in and absence out. */
  base: number;
  internHours: number;
  outHours: number;
  committed: number;
  gap: number;
  utilisation: number | null;
  status: LoadStatus;
  fte: number;
  rolesPerFte: number | null;
  aaaEquivalents: number | null;
  aaaCeiling: number | null;
  topTierSharePct: number | null;
  guardrailBreached: boolean;
}

export function companyStats(
  clusters: ClusterSeed[],
  state: PrioritisationState,
  model: EffortModel,
  cap: CapacityAssumptions,
  portfolio: Portfolio
): CompanyStats {
  const effective = effectivePortfolio(portfolio, state.added);
  const demand = demandOf(model, effective);
  const base = grossHours(state.team) * capacityYield(cap);
  const stats = allClusterStats(clusters, state, model, cap);
  const internHours = stats.reduce((s, c) => s + c.internHours, 0);
  const outHours = stats.reduce((s, c) => s + c.outHours, 0);
  /**
   * Hours offered by someone who is on the team but not planned as capacity. Unlike a transfer
   * between two planned recruiters — which nets to zero company-wide — this is capacity the plan
   * did not have, so it is the third lever that moves the company total.
   */
  const volunteered = volunteeredHours(state);
  const committed = base + internHours - outHours + volunteered;
  const utilisation = committed > 0 ? (demand / committed) * 100 : null;
  const aaa = tierHours(model, 'AAA');
  const fte = fteHours(cap) > 0 ? committed / fteHours(cap) : 0;
  const openRoles = openRolesOf(effective);
  const topTier = tierLoad(model, effective, 'AAA') + tierLoad(model, effective, 'A');
  const topTierSharePct = committed > 0 ? (topTier / committed) * 100 : null;
  return {
    volunteered,
    portfolio: effective,
    openRoles,
    demand,
    base,
    internHours,
    outHours,
    committed,
    gap: demand - committed,
    utilisation,
    status: statusOf(utilisation),
    fte,
    rolesPerFte: fte > 0 ? openRoles / fte : null,
    aaaEquivalents: aaa > 0 ? demand / aaa : null,
    aaaCeiling: aaa > 0 ? committed / aaa : null,
    topTierSharePct,
    guardrailBreached: topTierSharePct != null && topTierSharePct > FIREFIGHTING_GUARDRAIL_PCT,
  };
}

// ─────────────────────────── trade-offs and headroom ───────────────────────────

export interface TradeOff {
  id: string;
  label: string;
  roles: number;
}

/**
 * Every option closes the gap on its own. An option whose divisor is not positive is DROPPED rather
 * than shown as a negative role count — which is exactly what happens once someone edits the effort
 * model so the tiers are no longer strictly ordered.
 */
export function tradeOffs(gap: number, model: EffortModel): TradeOff[] {
  if (gap <= 0) return [];
  const h = (r: Rating) => tierHours(model, r);
  const steps: { id: string; label: string; divisor: number }[] = [
    { id: 'aaa-a', label: 'AAA → A', divisor: h('AAA') - h('A') },
    { id: 'a-b', label: 'A → B', divisor: h('A') - h('B') },
    { id: 'b-c', label: 'B → C', divisor: h('B') - h('C') },
    { id: 'park-c', label: 'park C', divisor: h('C') },
  ];
  return steps
    .filter((s) => s.divisor > 0)
    .map((s) => ({ id: s.id, label: s.label, roles: Math.ceil(gap / s.divisor) }));
}

/** How many further roles of each rating a positive spare absorbs whole. */
export function absorbable(spare: number, model: EffortModel): { rating: Rating; roles: number }[] {
  if (spare <= 0) return [];
  return RATINGS.map((r) => {
    const h = tierHours(model, r);
    return { rating: r, roles: h > 0 ? Math.floor(spare / h) : 0 };
  });
}

export interface NextRoleImpact {
  rating: Rating;
  cost: number;
  utilisation: number | null;
  mustFree: number;
}

export function nextRoleImpact(
  demand: number,
  committed: number,
  model: EffortModel
): NextRoleImpact[] {
  return RATINGS.map((r) => {
    const cost = tierHours(model, r);
    const nd = demand + cost;
    return {
      rating: r,
      cost,
      utilisation: committed > 0 ? (nd / committed) * 100 : null,
      mustFree: Math.max(0, nd - committed),
    };
  });
}

/** One AAA role costs this many roles of each lower rating. */
export function exchangeRate(model: EffortModel): { rating: Rating; ratio: number }[] {
  const aaa = tierHours(model, 'AAA');
  return RATINGS.filter((r) => r !== 'AAA')
    .map((r) => ({ rating: r, ratio: tierHours(model, r) > 0 ? aaa / tierHours(model, r) : NaN }))
    .filter((x) => isFinite(x.ratio));
}

// ─────────────────────────── suggested balance ───────────────────────────

/**
 * Two levers, deliberately ranked — and one it will never touch.
 *
 * It never volunteers an unplanned person's hours (see below). Unassigned intern capacity comes
 * FIRST because it adds hours instead of relocating them: no
 * handover, no hiring-manager renegotiation, no cold start on a live pipeline. Only when the pool
 * is spent does the engine lend hours between clusters, taking them from the LOWEST-utilisation
 * cluster with spare rather than the one with the most absolute headroom, so the hours come from
 * whoever is least stretched. A donor is never pushed past its own ceiling; when no donor can
 * cover the gap the caller is told, because the answer is then a rating trade-off, not support.
 */
export function suggestBalance(
  clusters: ClusterSeed[],
  state: PrioritisationState,
  model: EffortModel,
  cap: CapacityAssumptions
): { state: PrioritisationState; unresolved: string[] } {
  const next: PrioritisationState = {
    ...state,
    interns: { ...state.interns },
    allocations: [...state.allocations],
  };
  const worstOver = () =>
    allClusterStats(clusters, next, model, cap)
      .filter((c) => c.spare < -0.005)
      .sort((a, b) => a.spare - b.spare)[0];

  for (let guard = 0; guard < 8; guard += 1) {
    const free = poolAvailableFrom(next, cap) - poolAllocated(next, clusters);
    if (free <= 0.005) break;
    const target = worstOver();
    if (!target) break;
    const step = Math.min(0.5, free);
    next.interns[target.key] = Math.round(((next.interns[target.key] || 0) + step) * 100) / 100;
  }

  for (let guard = 0; guard < 12; guard += 1) {
    const target = worstOver();
    if (!target) break;
    const need = Math.ceil(-target.spare * 2) / 2;
    const donor = allClusterStats(clusters, next, model, cap)
      .filter((c) => c.key !== target.key && c.spare > 0.005)
      .sort((a, b) => (a.utilisation ?? 0) - (b.utilisation ?? 0))[0];
    if (!donor) break;
    const lend = Math.min(need, Math.floor(donor.spare * 2) / 2);
    if (lend < 0.5) break;
    // Only PLANNED recruiters are auto-suggested. Someone whose time is deliberately out of the
    // plan — the Head of TA — is never volunteered by the engine: offering those hours is a
    // decision a person takes, not one a suggestion makes on their behalf.
    const person = next.team
      .map((p, i) => ({ p, i, left: personCapacity(p, cap) - hoursLentBy(next, i) }))
      .filter((e) => e.p.cluster === donor.key && e.p.hours > 0 && isPlanned(e.p))
      .sort((a, b) => b.left - a.left)[0];
    if (!person) break;
    next.allocations.push({ from: person.i, to: target.key, hours: lend, focus: '' });
  }

  const unresolved = allClusterStats(clusters, next, model, cap)
    .filter((c) => c.spare < -0.005)
    .map((c) => c.key);
  return { state: next, unresolved };
}

/** Validation for a hand-entered allocation. Returns an error message, or null when it is fine. */
export function validateAllocation(
  state: PrioritisationState,
  from: number,
  to: string,
  hours: number
): string | null {
  const person = state.team[from];
  if (!person) return 'Pick a recruiter.';
  if (!isFinite(hours) || hours <= 0) return 'Enter hours above zero.';
  if (person.hours === 0) return `${person.name} is on leave — set a working model first.`;
  // A planned recruiter cannot lend to the cluster they already carry — that nets to nothing. But
  // someone NOT planned can: their hours are in no cluster's capacity, so giving them to their own
  // cluster is real added capacity.
  if (isPlanned(person) && person.cluster === to) return `${person.name} already carries ${to}.`;
  return null;
}

export function addAllocation(state: PrioritisationState, a: Allocation): PrioritisationState {
  return { ...state, allocations: [...state.allocations, a] };
}

export function removeAllocation(state: PrioritisationState, index: number): PrioritisationState {
  return { ...state, allocations: state.allocations.filter((_, i) => i !== index) };
}

/** People lending more of their week than it holds, and donor clusters pushed over by lending. */
export function supportWarnings(
  clusters: ClusterSeed[],
  state: PrioritisationState,
  model: EffortModel,
  cap: CapacityAssumptions
): { overLent: string[]; donorsOver: string[]; poolOverAllocated: number } {
  const overLent = state.team
    .map((p, i) => ({ p, lent: hoursLentBy(state, i), cap: personCapacity(p, cap) }))
    .filter((e) => e.lent > e.cap + 0.005)
    .map((e) => e.p.name);
  const donorsOver = allClusterStats(clusters, state, model, cap)
    .filter((c) => c.lent > 0 && c.spare < -0.005)
    .map((c) => c.key);
  // `lent` already excludes unplanned lenders, so a cluster is never reported as a stretched donor
  // on account of hours it never held.
  // The DERIVED pool, never `state.poolCount`: with the pool coming off the team's intern rows,
  // a stale typed count read as zero and reported every valid intern allocation as over-allocated.
  const over = poolAllocated(state, clusters) - poolAvailableFrom(state, cap);
  return { overLent, donorsOver, poolOverAllocated: over > 0.005 ? over : 0 };
}
